import type {
  ActivityId,
  BidId,
  DeliverableId,
  FileId,
  OfferId,
  OrganizationId,
  PackageId,
  ProjectId,
  ProjectServiceId,
  TeamId,
  UserId,
} from '@mntn-dev/domain-types'
import type { EmptyObject } from 'type-fest'
import type {
  AccountRouteAttributes,
  BreadCrumbRouteAttributes,
} from './attributes/types.ts'
import { register } from './register.ts'
import type { DashboardSortBy, SortDirection } from './types/sort-types.ts'
import type { DashboardPageTab, ProjectPageTab } from './types/tab-types.ts'

const routes = [
  // Unprotected

  register('/auth/logout').toRoute({}),

  register('/login').toRoute<
    EmptyObject,
    { returnTo?: string; emailAddress?: string }
  >({}),

  register('/register').toRoute<
    EmptyObject,
    { returnTo?: string; emailAddress?: string }
  >({}),

  register('/join').toRoute<
    EmptyObject,
    { returnTo?: string; emailAddress?: string }
  >({}),

  // Protected

  register('/')
    .protected()
    .toRoute({
      name: 'dashboard',
    }),

  register('/account')
    .protected()
    .toRoute<EmptyObject, EmptyObject, AccountRouteAttributes>({
      name: 'account',
    }),

  register('/account/teams')
    .protected()
    .toRoute<EmptyObject, { teamId: TeamId }, AccountRouteAttributes>({
      name: 'teams',
      tab: 'teams',
    }),

  register('/account/profile')
    .protected()
    .toRoute<EmptyObject, EmptyObject, AccountRouteAttributes>({
      name: 'profile',
      tab: 'profile',
    }),

  register('/account/organizations')
    .protected()
    .authorization(({ permissions }) =>
      permissions.includes('customer-organization:administer')
    )
    .toRoute<EmptyObject, EmptyObject, AccountRouteAttributes>({
      tab: 'organizations',
      name: 'organizations',
    }),

  register('/account/organizations/:organizationId')
    .protected()
    .authorization(
      ({ multiTeam, permissions }) =>
        multiTeam &&
        (permissions.includes('own-organization:administer') ||
          permissions.includes('customer-organization:administer'))
    )
    .toRoute<
      { organizationId: OrganizationId },
      EmptyObject,
      AccountRouteAttributes
    >({
      tab: ['organization', 'organizations'],
      name: 'organization',
    }),

  register('/account/organizations/:organizationId/billing')
    .protected()
    .authorization(
      ({ permissions }) =>
        permissions.includes('own-organization:administer') ||
        permissions.includes('customer-organization:administer')
    )
    .toRoute<
      { organizationId: OrganizationId },
      EmptyObject,
      AccountRouteAttributes
    >({ tab: 'billing', name: 'billing' }),

  register('/account/organizations/:organizationId/billing/methods')
    .protected()
    .authorization(
      ({ permissions }) =>
        permissions.includes('own-organization:administer') ||
        permissions.includes('customer-organization:administer')
    )
    .toRoute<
      { organizationId: OrganizationId },
      EmptyObject,
      AccountRouteAttributes
    >({ tab: 'billing', name: 'paymentMethods' }),

  register('/account/organizations/:organizationId/billing/methods/add-method')
    .protected()
    .authorization(
      ({ permissions }) =>
        permissions.includes('own-organization:administer') ||
        permissions.includes('customer-organization:administer')
    )
    .toRoute<
      { organizationId: OrganizationId },
      EmptyObject,
      AccountRouteAttributes
    >({ tab: 'billing', name: 'addPaymentMethod' }),

  register('/account/organizations/:organizationId/payments')
    .protected()
    .authorization(
      ({ permissions }) =>
        permissions.includes('own-organization:administer') ||
        permissions.includes('customer-organization:administer')
    )
    .toRoute<
      { organizationId: OrganizationId },
      EmptyObject,
      AccountRouteAttributes
    >({ tab: 'payments', name: 'payments' }),

  register('/account/organizations/:organizationId/payments/setup')
    .protected()
    .authorization(
      ({ permissions }) =>
        permissions.includes('own-organization:administer') ||
        permissions.includes('customer-organization:administer')
    )
    .toRoute<
      { organizationId: OrganizationId },
      EmptyObject,
      AccountRouteAttributes
    >({ tab: 'payments', name: 'paymentsSetup' }),

  register('/account/organizations/:organizationId/teams')
    .protected()
    .authorization(({ multiTeam }) => multiTeam)
    .toRoute<
      { organizationId: OrganizationId },
      { teamId: TeamId },
      AccountRouteAttributes
    >({
      name: 'teams',
      tab: 'teams',
    }),

  register('/account/organizations/:organizationId/teams/:teamId')
    .protected()
    .authorization(({ multiTeam }) => !multiTeam)
    .toRoute<
      { organizationId: OrganizationId; teamId: TeamId },
      EmptyObject,
      AccountRouteAttributes
    >({
      name: 'team',
      tab: ['team', 'teams'],
    }),

  register('/account/organizations/:organizationId/users')
    .protected()
    .authorization(
      ({ permissions }) =>
        permissions.includes('own-organization:administer') ||
        permissions.includes('customer-organization:administer')
    )
    .toRoute<
      { organizationId: OrganizationId },
      EmptyObject,
      AccountRouteAttributes
    >({ name: 'users', tab: 'users' }),

  register('/account/organizations/:organizationId/users/:userId')
    .protected()
    .authorization(
      ({ permissions }) =>
        permissions.includes('own-organization:administer') ||
        permissions.includes('customer-organization:administer')
    )
    .toRoute<
      { organizationId: OrganizationId; userId: UserId },
      EmptyObject,
      AccountRouteAttributes
    >({ name: 'user', tab: 'users' }),

  register('/dashboard')
    .protected([['project:view']])
    .toRoute<
      EmptyObject,
      {
        search?: string
        sortBy?: DashboardSortBy
        sortDir?: SortDirection
        tab?: DashboardPageTab
      }
    >({}),

  register('/offers').protected().toRoute({}),

  register('/offers/:offerId').protected().toRoute<{ offerId: OfferId }>({}),

  register('/packages').protected().toRoute({}),

  register('/packages/:packageId')
    .protected()
    .toRoute<{ packageId: PackageId }>({}),

  register('/packages/services').protected().toRoute({}),

  register('/projects').protected().toRoute({}),

  register('/projects/:projectId')
    .protected()
    .toRoute<
      { projectId: ProjectId },
      { tab?: ProjectPageTab; activityId?: ActivityId }
    >({}),

  register('/projects/:projectId/bid/:bidId')
    .protected()
    .toRoute<{ projectId: ProjectId; bidId: BidId }>({}),

  register('/projects/:projectId/bid/:bidId/edit')
    .protected()
    .toRoute<{ projectId: ProjectId; bidId: BidId }>({}),

  register('/projects/:projectId/services/:projectServiceId')
    .protected()
    .toRoute<{
      projectId: ProjectId
      projectServiceId: ProjectServiceId
    }>({}),

  register('/projects/:projectId/match')
    .protected()
    .toRoute<{ projectId: ProjectId }>({}),

  register('/projects/:projectId/match/maker/:userId').protected().toRoute<{
    projectId: ProjectId
    userId: UserId
  }>({}),

  register('/projects/:projectId/browse').protected().toRoute<{
    projectId: ProjectId
  }>({}),

  register('/projects/:projectId/change-order').protected().toRoute<
    {
      projectId: ProjectId
    },
    EmptyObject,
    BreadCrumbRouteAttributes
  >({
    name: 'change-order',
  }),

  register('/projects/:projectId/review/post-production/:deliverableId')
    .protected()
    .toRoute<{
      projectId: ProjectId
      deliverableId: DeliverableId
    }>({}),

  register('/projects/:projectId/files').protected().toRoute<{
    projectId: ProjectId
  }>({}),

  register('/projects/:projectId/files/:fileId').protected().toRoute<{
    projectId: ProjectId
    fileId: FileId
  }>({}),

  register('/projects/new').protected().toRoute({}),

  // Testing

  register('/foo')
    .protected([['project:view']])
    .toRoute<EmptyObject, { baz: string }>({}),

  register('/foo/:fooId').toRoute<{ fooId: string }, { baz: string }>({}),

  register('/foo/:fooId/bar/:barId').toRoute<
    { fooId: string; barId: string },
    { baz: string; qux: string; corge?: string }
  >({}),
]

export default routes
